import React from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "./Logo";
import { launchCheckout } from "../lib/upsell";

export const BG = "#FAF7EF";
export const BG_ALT = "#FFFFFF";
export const INK = "#141414";
export const LIME = "#D9F154";
export const BLUE = "#3B5EDF";

const NAV = [
  { to: "/programas", label: "Programas" },
  { to: "/intangibles", label: "Intangibles" },
  { to: "/toolkit", label: "Toolkit" },
  { to: "/certificacion", label: "Certificación" },
  { to: "/circle", label: "The Circle" },
  { to: "/ernesto", label: "Ernesto" },
];

export const chromeCss = `
  @import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Archivo+Black&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { overflow-x: hidden; width: 100%; background: ${BG}; scroll-behavior: smooth; scroll-padding-top: 90px; }
  body { font-family: 'Archivo', sans-serif; color: ${INK}; -webkit-font-smoothing: antialiased; }
  ::selection { background: ${LIME}; color: ${INK}; }
  .cg-mono { font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; }
  .cg-h1 { font-weight: 900; letter-spacing: -0.045em; line-height: 0.96; font-size: clamp(40px, 6.5vw, 88px); }
  .cg-h2 { font-weight: 900; letter-spacing: -0.035em; line-height: 1.02; font-size: clamp(30px, 4vw, 54px); }
  .cg-h3 { font-weight: 800; letter-spacing: -0.02em; line-height: 1.05; font-size: clamp(22px, 2.4vw, 30px); }
  .cg-btn {
    background: ${LIME}; color: ${INK}; padding: 16px 30px; font-family: 'Archivo', sans-serif;
    font-size: 12px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
    border: 2px solid ${INK}; border-radius: 14px; cursor: pointer; box-shadow: 4px 4px 0 ${INK};
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease; display: inline-block;
    text-decoration: none; text-align: center; white-space: nowrap;
  }
  .cg-btn:hover:not(:disabled) { transform: translate(2px,2px); box-shadow: 2px 2px 0 ${INK}; }
  .cg-btn:disabled { opacity: 0.6; cursor: default; }
  .cg-btn-blue { background: ${BLUE}; color: #FFFFFF; }
  .cg-btn-outline { background: transparent; box-shadow: none; }
  .cg-btn-outline:hover:not(:disabled) { background: ${INK}; color: ${BG}; transform: none; box-shadow: none; }
  .cg-card {
    background: ${BG_ALT}; border: 2px solid ${INK}; border-radius: 18px; box-shadow: 5px 5px 0 ${INK};
    padding: 32px; display: flex; flex-direction: column;
  }
  .cg-err { color: #C2410C; font-weight: 700; font-size: 12.5px; margin-top: 10px; }
  .cg-eyebrow { color: ${BLUE}; }
  .cg-link { color: ${INK}; text-decoration: none; font-weight: 700; }
  .cg-nav-link { font-family: 'Archivo', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 0.02em; color: ${INK}; text-decoration: none; opacity: 0.72; transition: opacity 0.2s; }
  .cg-nav-link:hover { opacity: 1; }
  @media (max-width: 860px) { .cg-nav-menu { display: none !important; } }
`;

export function BuyButton({ lookupKey, children, testid, className = "", style }) {
  return (
    <button
      type="button"
      className={`cg-btn ${className}`}
      onClick={() => launchCheckout(lookupKey)}
      data-testid={testid}
      style={style}
    >
      {children}
    </button>
  );
}

export function PriceTag({ product, style }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", ...style }}>
      <span style={{ fontWeight: 900, fontSize: 34, letterSpacing: "-0.03em" }}>
        {product.usd}{product.interval ? <span style={{ fontSize: 15, fontWeight: 700, opacity: 0.6 }}>/{product.interval}</span> : null} USD
      </span>
      <span className="cg-mono" style={{ opacity: 0.55, fontSize: 11 }}>≈ {product.mxn}</span>
    </div>
  );
}

export function SiteNav({ active }) {
  return (
    <nav
      style={{
        position: "sticky", top: 0, zIndex: 40, background: `${BG}dd`, backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(20,20,20,0.08)", padding: "14px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20,
      }}
      data-testid="site-nav"
    >
      <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: INK }}>
        <LogoMark size={70} />
      </Link>
      <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
        <div className="cg-nav-menu" style={{ display: "flex", gap: 26, alignItems: "center" }}>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="cg-nav-link" data-testid={`nav-${n.label.toLowerCase()}`}
              style={active === n.to ? { opacity: 1, color: BLUE } : undefined}>
              {n.label}
            </Link>
          ))}
        </div>
        <Link to="/programas" className="cg-btn" style={{ padding: "11px 20px", fontSize: 10 }} data-testid="nav-pricing">
          Ver precios
        </Link>
      </div>
    </nav>
  );
}

export function SiteFooter() {
  return (
    <footer
      style={{ borderTop: "1px solid rgba(20,20,20,0.08)", padding: "44px 40px", maxWidth: 1300, margin: "0 auto",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}
      data-testid="site-footer"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <LogoMark size={44} />
        <p style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.5 }}>
          the debt guy · © 2026 · ernesto gómez berjón
        </p>
      </div>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
        {NAV.map((n) => (
          <Link key={n.to} to={n.to} className="cg-nav-link" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.18em" }}>{n.label}</Link>
        ))}
        <Link to="/privacidad" className="cg-nav-link" data-testid="footer-privacy-link" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.18em" }}>Privacidad</Link>
        <Link to="/terminos" className="cg-nav-link" data-testid="footer-terms-link" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.18em" }}>Términos</Link>
      </div>
    </footer>
  );
}

export default function PageChrome({ children, active, testid = "page" }) {
  return (
    <div style={{ background: BG, color: INK, minHeight: "100vh", overflowX: "hidden" }} data-testid={testid}>
      <style>{chromeCss}</style>
      <SiteNav active={active} />
      {children}
      <SiteFooter />
    </div>
  );
}
